import { createContext, useEffect, useState } from 'react';
import type { API } from '@/api';
import type { ReactNode } from 'react';
import { LOUNGES } from '@/constants/area';
import { ROLES } from '@/constants/users';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { API_ENDPOINTS, useApiRequest } from '@/hooks/useApiRequest';
import { useAuth } from '@/hooks/useAuth';

type UserData = API['GET']['UserMe']['response']['data'];
type MembershipType = UserData['membership_type'];

interface UserContextType {
  user: UserData | null;
  isLoading: boolean;
  error: string | null;
  isAnaLoungeMember: boolean;
  // ライブ判定（user 由来。ログアウトで false になる）
  isSuiteLoungeMember: boolean;
  // スティッキー判定（直近の /users/me 成功値。ログアウト後も保持し、
  // ログアウト時のリダイレクト先(?entry=sl)判定に使う）
  wasSuiteLoungeMember: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export { UserContext };

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, role } = useAuth();
  // /users/me は general と共有端末(shared_device)で取得する。
  // staff は user 情報(email/membership)を使わないため取得しない。
  const shouldFetchMe =
    isAuthenticated && (role === ROLES.GENERAL || role === ROLES.SHARED_DEVICE);
  const [user, setUser] = useState<UserData | null>(null);
  const [isFetching, setIsFetching] = useState(shouldFetchMe);
  const [error, setError] = useState<string | null>(null);
  // 直近に取得できた会員種別。ログアウトでは敢えてクリアしない
  const [lastMembershipType, setLastMembershipType] =
    useState<MembershipType | null>(null);
  const { translateApiError } = useApiErrorTranslation();
  const { execute } = useApiRequest('GET', API_ENDPOINTS.GET.USER_ME);

  useEffect(() => {
    if (!shouldFetchMe) {
      setUser(null);
      setError(null);
      setIsFetching(false);
      return;
    }

    const fetchUser = async () => {
      setIsFetching(true);
      setError(null);

      const result = await execute({});

      if (result.ok) {
        const data = (result.data as API['GET']['UserMe']['response']).data;
        setUser(data);
        setLastMembershipType(data.membership_type);
      } else {
        const { message } = translateApiError(result.error);
        setError(message);
        setUser(null);
      }

      setIsFetching(false);
    };

    void fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchMe]);

  // isFetching は effect の中でしか true にならないため、AuthProvider が jwt を復元した直後の
  // 1レンダリングだけ「取得すべきなのに取得中でない」状態ができる(useState の初期値は
  // 復元前の shouldFetchMe=false で固定されている)。
  // 消費側がこの隙間を各自で埋めると取りこぼすので、Provider 側で「取得すべきだが結果がまだ無い」
  // 状態も取得中に含める。
  const isLoading =
    isFetching || (shouldFetchMe && user === null && error === null);

  const isAnaLoungeMember = user?.membership_type === LOUNGES.ANA_LOUNGE;
  const isSuiteLoungeMember = user?.membership_type === LOUNGES.SUITE_LOUNGE;
  const wasSuiteLoungeMember = lastMembershipType === LOUNGES.SUITE_LOUNGE;

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        error,
        isAnaLoungeMember,
        isSuiteLoungeMember,
        wasSuiteLoungeMember,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
